import { withAlpha, type Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import { clamp, type Point } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

/** How the gauge shows its value. */
export type RadialStyle =
  /** A wedge from the center, like a clock hand sweeping. */
  | "sweep"
  /** A ring, like a progress arc. */
  | "ring"
  /** A filled pie of the *remaining* value — the classic cooldown mask. */
  | "cooldown";

export interface RadialGaugeOptions extends ControlOptions {
  value?: number;
  maximum?: number;
  style?: RadialStyle;
  /** Where the sweep starts, in radians clockwise from 12 o'clock. */
  startAngle?: number;
  /** How far round it goes, in radians. Defaults to a full turn. */
  sweepAngle?: number;
  /** Ring thickness as a fraction of the radius. Ignored by other styles. */
  ringThickness?: number;
  fill?: Color | null;
  /** The unfilled part; null draws nothing behind the fill. */
  trackFill?: Color | null;
  /** Draw the remaining value in the middle. */
  showText?: boolean;
  textFormat?: (value: number, maximum: number) => string;
  font?: FontSpec | null;
}

const TAU = Math.PI * 2;

/**
 * A value drawn round a circle: an ability cooldown, a cast bar, a capture
 * timer, a channel.
 *
 * `cooldown` is the style most games mean — a dark wedge over the ability
 * icon that shrinks anticlockwise as the ability comes back — so it draws the
 * *remaining* portion rather than the elapsed one, and is meant to be laid
 * over an `ItemSlot` rather than to stand on its own.
 *
 * All three styles are one draw call apiece: they go through `rc.fillArc`,
 * which evaluates the wedge in the fragment shader. There is no rotation in
 * this renderer, and a curve built out of little rectangles would look it.
 */
export class RadialGauge extends Control {
  #value: number;
  #maximum: number;

  style: RadialStyle;
  startAngle: number;
  sweepAngle: number;
  ringThickness: number;
  fill: Color | null;
  trackFill: Color | null;
  showText: boolean;
  textFormat: ((value: number, maximum: number) => string) | null;
  font: FontSpec | null;

  constructor(options: RadialGaugeOptions = {}) {
    super({ width: 48, height: 48, tabStop: false, ...options });
    this.#maximum = Math.max(0, options.maximum ?? 1);
    this.#value = clamp(options.value ?? 0, 0, this.#maximum);
    this.style = options.style ?? "sweep";
    this.startAngle = options.startAngle ?? 0;
    this.sweepAngle = options.sweepAngle ?? TAU;
    this.ringThickness = clamp(options.ringThickness ?? 0.22, 0.02, 1);
    this.fill = options.fill ?? null;
    this.trackFill = options.trackFill ?? null;
    this.showText = options.showText ?? false;
    this.textFormat = options.textFormat ?? null;
    this.font = options.font ?? null;
  }

  get value(): number {
    return this.#value;
  }
  set value(v: number) {
    const next = clamp(Number.isFinite(v) ? v : this.#value, 0, this.#maximum);
    if (next === this.#value) return;
    this.#value = next;
    this.emit("valuechanged");
    this.emit("change");
    this.invalidate();
  }

  get maximum(): number {
    return this.#maximum;
  }
  set maximum(v: number) {
    this.#maximum = Math.max(0, Number.isFinite(v) ? v : this.#maximum);
    this.value = clamp(this.#value, 0, this.#maximum);
    this.invalidate();
  }

  /** Filled proportion, 0..1. */
  get fraction(): number {
    return this.#maximum > 0 ? this.#value / this.#maximum : 0;
  }

  /** True while a cooldown still has time on it. */
  get isRunning(): boolean {
    return this.#value > 0 && this.#value < this.#maximum;
  }

  get center(): Point {
    return { x: this.width / 2, y: this.height / 2 };
  }

  get radius(): number {
    return Math.min(this.width, this.height) / 2;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const center = this.center;
    const outer = this.radius;
    if (outer <= 0) return;

    const inner = this.style === "ring" ? outer * (1 - this.ringThickness) : 0;
    const ink = this.fill ?? p.accent;
    const span = clamp(this.sweepAngle, -TAU, TAU);

    theme.drawGaugeTrack(rc, {
      center: center,
      innerRadius: inner,
      outerRadius: outer,
      startAngle: this.startAngle,
      endAngle: this.startAngle + span,
      trackColor: this.trackFill,
      ring: this.style === "ring",
    });

    if (this.style === "cooldown") {
      // The mask covers what is *left* to wait, and shrinks as it runs out.
      const remaining = 1 - this.fraction;
      if (remaining <= 0) return;
      rc.fillArc(
        center,
        0,
        outer,
        this.startAngle,
        this.startAngle + span * remaining,
        this.fill ?? withAlpha(p.desktop, 0.66),
      );
      if (this.showText) this.#paintText(rc, theme, center);
      return;
    }

    const filled = span * this.fraction;
    if (filled !== 0) {
      rc.fillArc(center, inner, outer, this.startAngle, this.startAngle + filled, ink);
    }
    if (this.showText) this.#paintText(rc, theme, center);
  }

  #paintText(rc: RenderContext, theme: Theme, center: Point): void {
    const font = this.font ?? theme.metrics.font;
    const remaining = this.#maximum - this.#value;
    const text = this.textFormat
      ? this.textFormat(this.#value, this.#maximum)
      : formatSeconds(remaining);
    if (!text) return;
    const width = rc.measureText(text, font).width;
    rc.drawText(text, center.x - width / 2, center.y, font, theme.palette.text, {
      baseline: "middle",
    });
  }
}

/**
 * A cooldown readout: "12" for a long wait, "3.4" once it is close, nothing
 * once it is done. The switch to a decimal near the end is what lets a player
 * time an ability off the number rather than off the sweep.
 */
export function formatSeconds(seconds: number): string {
  if (seconds <= 0) return "";
  return seconds < 1 ? seconds.toFixed(1) : String(Math.ceil(seconds));
}
